// Utility functions for handling regional links

/**
 * Check if current page is in Canadian context
 */
export function isCanadianContext(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/ca');
}

/**
 * Convert a link to maintain regional context
 * @param href - Original link href
 * @returns Modified href that maintains regional context
 */
export function maintainRegionalContext(href: string): string {
  if (!href || typeof href !== 'string') return href;
  
  // Skip external links, anchors, and special paths
  if (
    href.startsWith('http') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#') ||
    href.startsWith('/api/') ||
    href.startsWith('/_next/') ||
    href.startsWith('/static/') ||
    href.startsWith('/images/') ||
    href.startsWith('/videos/') ||
    href.startsWith('/fonts/') ||
    href.startsWith('/json/') ||
    href.startsWith('/styles/')
  ) {
    return href;
  }
  
  // Handle /#anchor links - convert to #anchor
  if (href.startsWith('/#')) {
    return href.substring(1);
  }

  const isCanadian = isCanadianContext();
  
  // Handle homepage
  if (href === '/' || href === '') {
    return isCanadian ? '/ca' : '/';
  }
  
  // Remove leading slash for processing
  const cleanHref = href.startsWith('/') ? href.substring(1) : href;
  
  // If we're in Canadian context and link doesn't start with 'ca/'
  if (isCanadian && !cleanHref.startsWith('ca/')) {
    return `/ca/${cleanHref}`;
  }
  
  // If we're in US context and link starts with 'ca/'
  if (!isCanadian && cleanHref.startsWith('ca/')) {
    const withoutCa = cleanHref.substring(3);
    return withoutCa ? `/${withoutCa}` : '/';
  }
  
  // If we're in US context and link is exactly 'ca'
  if (!isCanadian && cleanHref === 'ca') {
    return '/';
  }
  
  // Return original href with leading slash
  return href.startsWith('/') ? href : `/${href}`;
}

/**
 * Hook to get regional link handler
 */
export function useRegionalLink() {
  return {
    maintainRegionalContext,
    isCanadianContext
  };
}
